import { useState, useEffect } from 'react';
import { getEmailPreview, getEmailTemplates, saveEmailTemplate, deleteEmailTemplate, sendAlert } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Send, Save, RotateCcw, FileText, Trash2, ChevronDown } from 'lucide-react';

const EmailPreviewModal = ({ open, onOpenChange, logId, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [texts, setTexts] = useState({ intro: '', advice: '', reco: '', expert: '' });
  const [defaults, setDefaults] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && logId) {
      setLoading(true);
      Promise.all([
        getEmailPreview(logId),
        getEmailTemplates()
      ]).then(([previewRes, templatesRes]) => {
        setPreview(previewRes.data);
        setDefaults(previewRes.data.default_texts);
        setTexts(previewRes.data.default_texts);
        setTemplates(templatesRes.data);
      }).catch(() => toast.error('Erreur chargement apercu'))
        .finally(() => setLoading(false));
    }
  }, [open, logId]);

  const handleReset = () => {
    setTexts({ ...defaults });
    toast.info('Textes réinitialisés');
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await sendAlert(logId, texts);
      if (res.data.success) {
        toast.success('Alerte envoyée au client');
        onSent?.(logId);
        onOpenChange(false);
      } else {
        toast.error("Echec de l'envoi");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const res = await saveEmailTemplate({
        name: templateName,
        texts,
        trigger_type: preview?.trigger_type || 'max'
      });
      setTemplates(prev => [...prev, res.data]);
      setTemplateName('');
      setShowSaveInput(false);
      toast.success('Template sauvegardé');
    } catch (error) {
      toast.error('Erreur sauvegarde');
    }
  };

  const handleLoadTemplate = (tpl) => {
    setTexts(tpl.texts);
    setShowTemplates(false);
    toast.success(`Template "${tpl.name}" charge`);
  };

  const handleDeleteTemplate = async (tplId, e) => {
    e.stopPropagation();
    try {
      await deleteEmailTemplate(tplId);
      setTemplates(prev => prev.filter(t => t.id !== tplId));
      toast.success('Template supprimé');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const stripHtml = (html) => html?.replace(/<[^>]*>/g, '') || '';

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#BFC6CC]/30 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#191D1E]">Apercu et personnalisation de l'email</DialogTitle>
          <DialogDescription className="text-[#687175]">
            {preview ? `Pour ${preview.client_name} - ${preview.contract_name}` : 'Chargement...'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-[#687175]">Chargement de l'apercu...</div>
        ) : preview ? (
          <div className="space-y-5 mt-2">
            {/* Alert info bar */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F6F9] border border-[#BFC6CC]/20">
              <div className={`w-3 h-3 rounded-full ${preview.trigger_type === 'min' ? 'bg-[#10D183]' : 'bg-[#EF4444]'}`} />
              <div className="text-sm text-[#3C4649]">
                <strong>{preview.trigger_type === 'min' ? 'Prix bas' : 'Prix haut'}</strong>
                {' '}{preview.contract_name} — {preview.trigger_price}€ (seuil: {preview.threshold}€)
              </div>
            </div>

            {/* Template actions */}
            <div className="flex gap-2 flex-wrap">
              <div className="relative">
                <Button variant="outline" size="sm" onClick={() => setShowTemplates(!showTemplates)}
                  className="text-[#687175] border-[#BFC6CC]/30" data-testid="load-template-btn">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Charger un template
                  <ChevronDown className="w-3.5 h-3.5 ml-1" />
                </Button>
                {showTemplates && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-[#BFC6CC]/30 rounded-xl shadow-lg z-50 min-w-[240px] py-1">
                    {templates.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-[#687175]">Aucun template sauvegardé</div>
                    ) : (
                      templates.map(tpl => (
                        <div key={tpl.id} className="flex items-center justify-between px-4 py-2 hover:bg-[#F5F6F9] cursor-pointer"
                          onClick={() => handleLoadTemplate(tpl)} data-testid={`template-${tpl.id}`}>
                          <div>
                            <p className="text-sm font-medium text-[#191D1E]">{tpl.name}</p>
                            <p className="text-xs text-[#687175]">{tpl.trigger_type === 'min' ? 'Prix bas' : 'Prix haut'}</p>
                          </div>
                          <button onClick={(e) => handleDeleteTemplate(tpl.id, e)}
                            className="p-1 text-[#939BA0] hover:text-red-500 rounded" data-testid={`delete-template-${tpl.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={handleReset}
                className="text-[#687175] border-[#BFC6CC]/30" data-testid="reset-texts-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Réinitialiser
              </Button>
            </div>

            {/* Editable text fields */}
            <div className="space-y-4">
              <div>
                <Label className="text-[#191D1E] font-semibold">Introduction</Label>
                <p className="text-xs text-[#687175] mb-1.5">Texte d'accroche expliquant le franchissement du seuil</p>
                <textarea value={stripHtml(texts.intro)}
                  onChange={(e) => setTexts({ ...texts, intro: e.target.value })}
                  className="w-full p-3 rounded-lg bg-[#F5F6F9] border border-[#BFC6CC] text-[#191D1E] text-sm resize-none focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF] outline-none"
                  rows={3} data-testid="edit-intro" />
              </div>
              <div>
                <Label className="text-[#191D1E] font-semibold">Notre analyse</Label>
                <p className="text-xs text-[#687175] mb-1.5">Contexte et impact sur le contrat du client</p>
                <textarea value={stripHtml(texts.advice)}
                  onChange={(e) => setTexts({ ...texts, advice: e.target.value })}
                  className="w-full p-3 rounded-lg bg-[#F5F6F9] border border-[#BFC6CC] text-[#191D1E] text-sm resize-none focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF] outline-none"
                  rows={3} data-testid="edit-advice" />
              </div>
              <div>
                <Label className="text-[#191D1E] font-semibold">Notre recommandation</Label>
                <p className="text-xs text-[#687175] mb-1.5">Conseil d'action pour le client</p>
                <textarea value={stripHtml(texts.reco)}
                  onChange={(e) => setTexts({ ...texts, reco: e.target.value })}
                  className="w-full p-3 rounded-lg bg-[#F5F6F9] border border-[#BFC6CC] text-[#191D1E] text-sm resize-none focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF] outline-none"
                  rows={3} data-testid="edit-reco" />
              </div>
              <div>
                <Label className="text-[#191D1E] font-semibold">Message de l'expert</Label>
                <p className="text-xs text-[#687175] mb-1.5">Message personnalise de l'agence</p>
                <textarea value={stripHtml(texts.expert)}
                  onChange={(e) => setTexts({ ...texts, expert: e.target.value })}
                  className="w-full p-3 rounded-lg bg-[#F5F6F9] border border-[#BFC6CC] text-[#191D1E] text-sm resize-none focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF] outline-none"
                  rows={3} data-testid="edit-expert" />
              </div>
            </div>

            {/* Save template */}
            {showSaveInput ? (
              <div className="flex gap-2">
                <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                  className="bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="Nom du template..."
                  data-testid="template-name-input" />
                <Button size="sm" onClick={handleSaveTemplate} className="bg-[#397DFF] hover:bg-[#397DFF]/90 text-white whitespace-nowrap"
                  data-testid="save-template-confirm-btn">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> Sauvegarder
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowSaveInput(false); setTemplateName(''); }}
                  className="text-[#687175] border-[#BFC6CC]/30">Annuler</Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowSaveInput(true)}
                className="text-[#687175] border-[#BFC6CC]/30" data-testid="save-template-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Sauvegarder comme template
              </Button>
            )}

            {/* Send button */}
            <Button onClick={handleSend} disabled={sending} className="w-full bg-[#397DFF] hover:bg-[#397DFF]/90 text-white py-6 text-base"
              data-testid="preview-send-btn">
              <Send className={`w-4 h-4 mr-2 ${sending ? 'animate-pulse' : ''}`} />
              {sending ? 'Envoi en cours...' : `Envoyer l'email a ${preview.client_name}`}
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreviewModal;
